import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  catchError,
  map,
  switchMap,
} from "rxjs/operators";
import { of } from "rxjs";
import { OrganizationAdminActions } from "../../actions/organization-admin/organization-admin.actions";
import { OrganizationAdminService } from "../../../services/organization-admin.service";
import { OrganizationDetailsService } from "../../../services/organization-details.service";
import { DropdownsService } from "../../../services/dropdowns.service";
import { routerRequestActionTo } from '../../actions/function-actions/router-request.action';

@Injectable()
export class OrganizationAdminEffects {

  constructor(
    private actions$: Actions,
    private organizationAdminService: OrganizationAdminService,
    private organizationDetailsService: OrganizationDetailsService,
    private dropdownsService: DropdownsService
  ) {}

  // Load organizations when navigating to organization admin page
  getOrganizationsOnNavigate$ = createEffect(() => {
    return this.actions$.pipe(
      routerRequestActionTo('/organizationadmin'),
      switchMap(() => {
        return this.organizationAdminService.getOrganizations().pipe(
          map((organizations) => OrganizationAdminActions.loadOrganizationsSuccess({ organizations })),
          catchError((error) => of(OrganizationAdminActions.loadOrganizationsFailure({ error: error.message })))
        );
      })
    );
  });

  // Load organizations
  loadOrganizations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.loadOrganizations),
      switchMap(() => {
        return this.organizationAdminService.getOrganizations().pipe(
          map((organizations) => OrganizationAdminActions.loadOrganizationsSuccess({ organizations })),
          catchError((error) => of(OrganizationAdminActions.loadOrganizationsFailure({ error: error.message })))
        );
      })
    );
  });

  // Load organization types
  loadOrganizationTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.loadOrganizationTypes),
      switchMap(() => {
        return this.dropdownsService.getOrganizationTypes('').pipe(
          map((organizationTypes) => OrganizationAdminActions.loadOrganizationTypesSuccess({ organizationTypes })),
          catchError((error) => of(OrganizationAdminActions.loadOrganizationTypesFailure({ error: error.message })))
        );
      })
    );
  });

  // Load states
  loadStates$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.loadStates),
      switchMap(() => {
        return this.dropdownsService.getStates('').pipe(
          map((states) => OrganizationAdminActions.loadStatesSuccess({ states })),
          catchError((error) => of(OrganizationAdminActions.loadStatesFailure({ error: error.message })))
        );
      })
    );
  });

  // Add organization
  addOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.addOrganization),
      switchMap((action) => {
        return this.organizationAdminService.addOrganization(action.organization).pipe(
          map((organization) => OrganizationAdminActions.addOrganizationSuccess({ organization })),
          catchError((error) => of(OrganizationAdminActions.addOrganizationFailure({ error: error.message })))
        );
      })
    );
  });

  // Update organization
  updateOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.updateOrganization),
      switchMap((action) => {
        return this.organizationAdminService.updateOrganization(action.organization).pipe(
          map((organization) => OrganizationAdminActions.updateOrganizationSuccess({ organization })),
          catchError((error) => of(OrganizationAdminActions.updateOrganizationFailure({ error: error.message })))
        );
      })
    );
  });

  // Delete organization
  deleteOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.deleteOrganization),
      switchMap((action) => {
        return this.organizationAdminService.deleteOrganization(action.organizationId).pipe(
          map(() => OrganizationAdminActions.deleteOrganizationSuccess({ organizationId: action.organizationId })),
          catchError((error) => of(OrganizationAdminActions.deleteOrganizationFailure({ error: error.message })))
        );
      })
    );
  });

  // Set test organization status
  setTestOrganizationStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationAdminActions.setTestOrganizationStatus),
      switchMap((action) => {
        return this.organizationDetailsService.setTestOrganizationStatus(action.organizationId, action.isTestOrganization).pipe(
          map(() => OrganizationAdminActions.setTestOrganizationStatusSuccess({
            organizationId: action.organizationId,
            isTestOrganization: action.isTestOrganization,
          })),
          catchError((error) => of(OrganizationAdminActions.setTestOrganizationStatusFailure({ error: error.message })))
        );
      })
    );
  });
}
